import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { CHANNELS } from "../../shared/goals/channels";
import { PnRow } from "../../shared/phone/Row";
import { Sheet } from "../../shared/phone/Sheet";
import { useCan } from "../context/AuthContext";
import { closeSheet } from "../views/cc/home/parts";
import { Icon } from "./ui/Icon";

/**
 * New goal: a Meta ads goal by default, with the other kinds in a menu. LinkedIn and Google are not built
 * yet. Cold email is its own kind of goal, offered only on a product that gets it.
 */
export function NewGoalMenu({ to, coldEmail, label = "New goal", className = "btn" }: { to: string; coldEmail: boolean; label?: string; className?: string }) {
  const can = useCan();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };
  if (!can("editor")) return null;
  return (
    <div className="ngm" ref={ref}>
      <Link className={`${className} ngm-main`} to={to}>
        <Icon name="plus" />
        {label}
      </Link>
      <button type="button" className={`${className} ngm-more`} aria-haspopup="menu" aria-expanded={open} aria-label="Other kinds of goal" onClick={() => setOpen((o) => !o)}>
        <Icon name="chev" />
      </button>
      {open && (
        <div className="ngm-menu" role="menu">
          <button type="button" role="menuitem" className="ngm-item" onClick={() => go(to)}>
            <b>{CHANNELS.meta.label}</b>
            <span>{CHANNELS.meta.sub}</span>
          </button>
          <div role="menuitem" aria-disabled="true" className="ngm-item off">
            <b>LinkedIn ads</b>
            <span>Coming soon</span>
          </div>
          <div role="menuitem" aria-disabled="true" className="ngm-item off">
            <b>Google ads</b>
            <span>Coming soon</span>
          </div>
          {coldEmail && (
            <>
              <div className="ngm-sep" />
              <button type="button" role="menuitem" className="ngm-item" onClick={() => go(`${to}?channel=email`)}>
                <b>{CHANNELS.email.label}</b>
                <span>{CHANNELS.email.sub}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * New goal on a phone and an iPad: how the agent should reach people, as a sheet. The kinds not built yet
 * sit apart at the bottom.
 */
export function NewGoalSheet({ open, onClose, to, coldEmail }: { open: boolean; onClose: () => void; to: string; coldEmail: boolean }) {
  const navigate = useNavigate();
  // Why: the goal's first step opens once the sheet has slid away, so the two never move at once.
  const then = useRef<string | null>(null);
  const pick = (path: string) => {
    then.current = path;
    closeSheet();
  };
  return (
    <Sheet
      open={open}
      onClose={() => {
        onClose();
        if (then.current) navigate(then.current);
        then.current = null;
      }}
      title="New goal"
    >
      <p className="hm-sub">How should the agent reach people?</p>
      <div className="pn-grp">
        <PnRow onClick={() => pick(to)} icon={<Icon name="megaphone" />} title={CHANNELS.meta.label} sub={CHANNELS.meta.sub} chevron />
        {coldEmail && <PnRow onClick={() => pick(`${to}?channel=email`)} icon={<Icon name="mail" />} title={CHANNELS.email.label} sub={CHANNELS.email.sub} chevron />}
      </div>
      <div className="pn-cap hm-gap">Coming soon</div>
      <div className="pn-grp hm-soon">
        <PnRow icon={<Icon name="search" />} iconTone="plain" title="Google ads" />
        <PnRow icon={<Icon name="user" />} iconTone="plain" title="LinkedIn ads" />
      </div>
    </Sheet>
  );
}
